'use client';

import { forwardRef, useCallback, useMemo } from 'react';
import { parseShortcutKeys } from '@examdojo/core/tiptap-react-ui-components/lib/tiptap-utils';
import { useTiptapEditor } from '@examdojo/core/tiptap-react-ui-components/hooks/use-tiptap-editor';
import type { UseColorTextConfig } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/color-text-button';
import {
  COLOR_TEXT_SHORTCUT_KEY,
  useColorText,
} from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/color-text-button';
import type { ButtonProps } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Button } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/button';
import { Badge } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui-primitive/badge';


export interface ColorTextButtonProps extends Omit<ButtonProps, 'type'>, UseColorTextConfig {
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
}

export function ColorTextShortcutBadge({ shortcutKeys = COLOR_TEXT_SHORTCUT_KEY }: { shortcutKeys?: string }) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for applying text colors in a Tiptap editor.
 *
 * For custom button implementations, use the `useColorText` hook instead.
 */
export const ColorTextButton = forwardRef<HTMLButtonElement, ColorTextButtonProps>(
  (
    {
      editor: providedEditor,
      textColor,
      text,
      hideWhenUnavailable = false,
      onApplied,
      showShortcut = false,
      onClick,
      children,
      style,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const { isVisible, canColorText, isActive, handleColorText, label, shortcutKeys, Icon } = useColorText({
      editor,
      textColor,
      label: text || `Color text to ${textColor}`,
      hideWhenUnavailable,
      onApplied,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleColorText();
      },
      [handleColorText, onClick],
    );

    const buttonStyle = useMemo(
      () =>
        ({
          ...style,
          '--color-text-button-color': textColor,
        }) as React.CSSProperties,
      [textColor, style],
    );

    if (!isVisible) {
      return null;
    }

    return (
      <Button
        type="button"
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        role="button"
        tabIndex={-1}
        disabled={!canColorText}
        data-disabled={!canColorText}
        aria-label={label}
        aria-pressed={isActive}
        tooltip={label}
        onClick={handleClick}
        style={buttonStyle}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <span className="tiptap-button-color-text" style={{ color: textColor }}>
              <Icon className="tiptap-button-icon" style={{ color: textColor, flexGrow: 1 }} />
            </span>
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && <ColorTextShortcutBadge shortcutKeys={shortcutKeys} />}
          </>
        )}
      </Button>
    );
  },
);

ColorTextButton.displayName = 'ColorTextButton';
